/**
 * WHY AN ASK WAS NEVER SENT, AND WHAT THE PRACTICE DOES ABOUT IT —
 * `tasks/practice-documents.md` §25.3, §25.4, §23.1.
 *
 * ── WHY THIS IS ITS OWN MODULE ───────────────────────────────────────────────────────────────
 *
 * A skipped document request is answered in three different places — the patient-file documents
 * panel, the appointment, and (as a warning before it happens) the booking screen. §25.4's whole
 * argument is that `late`, `suppressed` and `unreachable` are not one fact with one remedy:
 *
 *   late         the rule's day had already passed when the booking was made   send it by hand
 *   suppressed   the practice said not to contact this patient about this      change the type, or
 *                booking — a suppressSending type, or notifications off         re-book with
 *                                                                               notifications on
 *   unreachable  the patient has no email address and no mobile number          edit the patient
 *
 * Three screens each inventing their own wording is three chances for one of them to tell a
 * receptionist to do the wrong thing. So the vocabulary lives here once, as pure functions, in the
 * same shape `clinical-timeline-documents.documentScoreLabel` uses for the identical reason.
 *
 * ── AND WHY IT IS NOT IN `patient-document-request.service.ts` ───────────────────────────────
 *
 * That file is owned by another wave working in parallel on the same branch, and a shared edit is
 * how two agents produce a merge that neither of them tested. The types below are therefore
 * DELIBERATELY LOOSE — `status?: string`, `skipReason?: string | null` — rather than reusing
 * `PatientDocumentRequestStatus`, whose union does not yet carry `'skipped'`. A structural
 * parameter type accepts the DTO as it stands today and as it stands after that union grows,
 * with no cast at any call site and nothing to unpick when the two files are reconciled.
 *
 * ── `sendNow` LIVES HERE FOR THE SAME REASON ─────────────────────────────────────────────────
 *
 * `POST /api/patient-document-requests/:id/send-now` is the endpoint §23.1 built so that a skip is
 * ACTIONABLE from the screen a practice meets it on: it re-schedules the row to now, clears
 * `skippedAt`, and DELIVERS inside the request rather than deferring to the next job run.
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { formatDocumentDate } from './patient-document-request.service';

/** The three values `CK_PatientDocumentRequests_skipReason` allows. */
export type DocumentSkipReason = 'late' | 'suppressed' | 'unreachable';

/**
 * The fields of a document request this module reads, and nothing else.
 *
 * Structural, not the DTO — see the header. `PatientDocumentRequestDto` satisfies it today
 * (`status` narrows to `string`, and the three skip fields are optional), so every call site
 * passes a request straight through.
 */
export interface DocumentSkipRow {
  id?: string;
  status?: string | null;
  skipReason?: string | null;
  skippedAt?: string | Date | null;
  dueAt?: string | Date | null;
  appointmentId?: string | null;
}

/** What a screen needs to draw one skipped row. `null` when the row was not skipped. */
export interface DocumentSkipDescription {
  reason: DocumentSkipReason | null;
  /** The chip. Short enough to sit beside a document name. */
  label: string;
  /** What happened, in a sentence, including the date the rule named where it is known. */
  why: string;
  /** WHAT TO DO. The half §25.4 exists for — the three reasons need three different actions. */
  remedy: string;
  /**
   * Whether "Send now" is worth offering.
   *
   * FALSE for `unreachable`, and that is the point rather than an omission. Send-now marks the row
   * `sent` and then delivers; with no email address and no mobile number there is no channel, so
   * the button would move the row into a state that claims a send that never happened — the exact
   * thing §25.3 stopped the JOB doing. The remedy for that row is the patient's contact details.
   */
  canSendNow: boolean;
}

/** `true` when this row is in the terminal state that means "the patient was never asked". */
export function isDocumentSkipped(row: DocumentSkipRow | null | undefined): boolean {
  return !!row && String(row.status) === 'skipped';
}

/**
 * The vocabulary. `null` for any row that is not skipped — a screen that drew a skip explanation
 * on a `sent` row would be describing a fact that is not true of it.
 *
 * A `skipped` row with NO reason is not treated as an error and not hidden either: §25.4 reserves
 * that state as the signal that something wrote a skip without saying why, and the honest answer
 * on screen is "we do not know why" plus the one remedy that always works.
 */
export function describeDocumentSkip(
  row: DocumentSkipRow | null | undefined,
): DocumentSkipDescription | null {
  if (!isDocumentSkipped(row)) return null;

  const ruleDay = formatDocumentDate((row && row.dueAt) as any);
  // `dueAt` is LEFT ALONE on a skip precisely so this sentence can be written (§24.3). When it is
  // missing the sentence simply drops the clause rather than inventing a date.
  const onItsDay = ruleDay ? ` It was due to go out on ${ruleDay}.` : '';

  switch (String((row && row.skipReason) || '')) {
    case 'late':
      return {
        reason: 'late',
        label: 'Not sent — booked too late',
        why: `This booking was made after the day the rule names, so it was never sent.${onItsDay}`,
        remedy: 'Send it now if the patient still needs it.',
        canSendNow: true,
      };
    case 'suppressed':
      return {
        reason: 'suppressed',
        label: 'Not sent — sending is off',
        why: 'This appointment type does not contact patients, or the booking was saved with '
          + `notifications switched off.${onItsDay}`,
        remedy: 'Change the appointment type, or re-book with notifications on. You can also send '
          + 'it now.',
        canSendNow: true,
      };
    case 'unreachable':
      return {
        reason: 'unreachable',
        label: 'Not sent — no way to reach the patient',
        why: 'This patient has no email address and no mobile number on file, so there was nothing '
          + `to send it to.${onItsDay}`,
        remedy: 'Add an email address or a mobile number to the patient, then send it.',
        canSendNow: false,
      };
    default:
      return {
        reason: null,
        label: 'Not sent',
        why: `This ask was never sent, and no reason was recorded against it.${onItsDay}`,
        remedy: 'Check the patient\'s contact details and the appointment type, then send it now.',
        canSendNow: true,
      };
  }
}

/**
 * True when the patient has no channel at all — the state that produces `unreachable` days later.
 *
 * §25.5's booking-time warning is built on this: the practice should learn it while the patient is
 * in front of them, not when a document silently fails to go out. Whitespace-only values count as
 * absent, because a space in the mobile-number field is what a half-finished capture leaves.
 */
export function patientHasNoChannel(
  patient: { email?: string | null; mobileNumber?: string | null } | null | undefined,
): boolean {
  if (!patient) return false;
  const email = (patient.email || '').trim();
  const mobile = (patient.mobileNumber || '').trim();
  return !email && !mobile;
}

@Injectable({ providedIn: 'root' })
export class PatientDocumentSkipService {

  private apiUrl = 'api/patient-document-requests';

  constructor(private http: HttpClient) { }

  private authOptions() {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }

  /**
   * §23.1's send-now: re-schedule to now and DELIVER inside the request.
   *
   * Not "queue it for the next job run" — that design was considered and rejected, because the
   * practice is usually pressing this precisely because the appointment is tomorrow.
   */
  sendNow(requestId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${requestId}/send-now`, {}, this.authOptions());
  }

  /**
   * This appointment's document requests.
   *
   * Read through the PATIENT endpoint and filtered here, deliberately: there is no by-appointment
   * endpoint, and `PatientDocumentRequests.appointmentId` is stamped on every row a booking raises
   * (§24.3), so the filter is exact. Adding a route for it would mean editing a controller another
   * wave owns.
   *
   * The comparison is case-insensitive on the string form because MSSQL hands back uppercase
   * `uniqueidentifier` values on some paths and the route parameter is whatever the client held.
   */
  loadForAppointment(patientId: string, appointmentId: string): Observable<DocumentSkipRow[]> {
    return this.http
      .get<any>(`${this.apiUrl}/patient/${patientId}`, this.authOptions())
      .pipe(map((result: any) => {
        const rows: any[] = (result && Array.isArray(result.data)) ? result.data : [];
        const wanted = String(appointmentId).toLowerCase();
        return rows.filter((row) => row && row.appointmentId
          && String(row.appointmentId).toLowerCase() === wanted);
      }));
  }
}
